import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { getFreightSocialMediaEdit, storeFreightSocialMedia, updateFreightSocialMedia } from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const MAX_ICON_BYTES = 4 * 1024 * 1024;
const ICON_EXT_RE = /\.(jpe?g|png|gif|webp)$/i;

const FreightSocialMediaFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/social_media/new", end: true });
  const { id: routeId } = useParams();
  const isNew = Boolean(newMatch);
  const entryId = isNew ? null : routeId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Social media", link: "/freight/social_media" },
      { label: isNew ? "New link" : `Edit #${entryId ?? ""}` }
    ],
    [roleLabel, isNew, entryId]
  );

  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [status, setStatus] = useState("1");
  const [iconPreviewUrl, setIconPreviewUrl] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [iconObjectUrl, setIconObjectUrl] = useState("");

  const loadEdit = useCallback(async () => {
    if (!entryId) return;
    setLoading(true);
    try {
      const res = await getFreightSocialMediaEdit(entryId);
      const d = res?.data?.socialMediaData;
      if (!d) throw new Error("Not found");
      setName(String(d.name ?? ""));
      setLink(String(d.link ?? ""));
      setOrderNo(d.order_no != null ? String(d.order_no) : "");
      setStatus(String(d.status ?? "1"));
      const u = d.icon_url != null ? String(d.icon_url).trim() : "";
      setIconPreviewUrl(u);
      setIconFile(null);
    } catch (e) {
      showError(e.message || "Load failed");
      navigate("/freight/social_media", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [entryId, navigate]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      setIconPreviewUrl("");
      setIconFile(null);
      return;
    }
    loadEdit();
  }, [isNew, loadEdit]);

  useEffect(() => {
    if (!iconFile) {
      setIconObjectUrl("");
      return undefined;
    }
    const u = URL.createObjectURL(iconFile);
    setIconObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [iconFile]);

  const onIconChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) {
      setIconFile(null);
      return;
    }
    if (f.size > MAX_ICON_BYTES) {
      showError(`Icon must be at most ${MAX_ICON_BYTES / (1024 * 1024)} MB`);
      return;
    }
    if (!ICON_EXT_RE.test(f.name)) {
      showError("Icon: only jpg, jpeg, png, gif, or webp");
      return;
    }
    setIconFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      showError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", trimmedName);
      fd.append("link", link.trim());
      fd.append("order_no", orderNo === "" ? "" : String(orderNo));
      fd.append("status", status);
      if (iconFile) fd.append("icon", iconFile);
      if (isNew) {
        await storeFreightSocialMedia(fd);
        showSuccess("Social link created");
      } else {
        fd.append("id", String(entryId));
        await updateFreightSocialMedia(fd);
        showSuccess("Social link updated");
      }
      navigate("/freight/social_media");
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumbs={breadcrumbs}>
        <div className="max-w-[720px] card offers-table p-6">Loading…</div>
      </DashboardLayout>
    );
  }

  const previewSrc = iconObjectUrl || iconPreviewUrl;

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[720px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="m-0">{isNew ? "New social link" : "Edit social link"}</h3>
            <Link to="/freight/social_media" className="btn-outline no-underline text-sm">
              ← Back to list
            </Link>
          </div>

          <form className="freight-admin-form space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required maxLength={255} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
              <input type="text" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
              <input type="number" min={0} value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="Optional" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
              <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,image/*" onChange={onIconChange} className="text-sm" />
              <p className="text-xs text-gray-500 mt-1 m-0">Optional on create. On edit, leave empty to keep the current icon.</p>
              {previewSrc ? (
                <div className="mt-2">
                  <span className="text-xs text-gray-600">Preview:</span>
                  <img src={previewSrc} alt="" className="mt-1 h-14 w-14 rounded-md border border-gray-200 object-contain bg-white" />
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : isNew ? "Create" : "Save changes"}
              </button>
              <Link to="/freight/social_media" className="btn-outline no-underline inline-flex items-center">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightSocialMediaFormPage;
